/**
 * The one timeline (TIMELINE R20, R22) as every surface draws it: now, dated today, then the end of
 * this month and of each later month, each labelled by its last day.
 */
import { core, debt as debtNs, formatDay, plan as planNs, type Day, type Decimal, type ForecastResult, type Position, type Series } from '@map-my-money/engine';
import type { Forecasted } from './run.ts';

export interface LinePoint {
  /** Today for the now point, else the month's last day. */
  readonly date: Day;
  readonly isNow: boolean;
  readonly netWorth: Decimal;
  readonly cash: Decimal;
  readonly investments: Decimal;
  /** Property equity plus the other assets' value, as the line's equity series counts them. */
  readonly equity: Decimal;
  readonly debt: Decimal;
}

export const lastDayOf = (bucket: { readonly end: Day }): Day => core.addDays(bucket.end, -1);

export const monthKey = (day: Day): string => formatDay(day).slice(0, 7);

/** Now as a line point. */
export const nowPoint = (now: Position, today: Day): LinePoint => ({
  date: today,
  isNow: true,
  netWorth: now.netWorth,
  cash: now.cash,
  investments: now.investments,
  equity: core.add(now.propertyEquity, now.assetValue),
  debt: now.debt,
});

/**
 * The line: now, then each month end after today. On a month's last day now is that month's end
 * (R21), so the month's own point is left out.
 */
export function linePoints(f: { readonly forecast: Pick<ForecastResult, 'cumulative' | 'now'>; readonly today: Day }): LinePoint[] {
  const c = f.forecast.cumulative;
  const months = c.netWorth.flatMap((bucket, i): LinePoint[] => {
    const date = lastDayOf(bucket);
    if (date <= f.today) return [];
    return [{
      date,
      isNow: false,
      netWorth: bucket.amount,
      cash: c.cash[i]?.amount ?? core.ZERO,
      investments: c.investment[i]?.amount ?? core.ZERO,
      equity: c.equity[i]?.amount ?? core.ZERO,
      debt: c.debt[i]?.amount ?? core.ZERO,
    }];
  });
  return [nowPoint(f.forecast.now, f.today), ...months];
}

/** The end of this month: now plus everything still to come in it (R21). */
export const monthEndPoint = (points: readonly LinePoint[]): LinePoint => {
  const first = points[0]!;
  const next = points[1];
  return next !== undefined && monthKey(next.date) === monthKey(first.date) ? next : first;
};

/**
 * One calendar month of the line as its bars show it (R22): money in, money out, the part of out
 * that stays yours (own investment payments, debt principal repaid) and growth (returns, match,
 * relief, value changes). in − out is the change in cash and the four add up to the change in net
 * worth, to the penny.
 */
export interface MonthFlows {
  /** The month's last day. */
  readonly date: Day;
  readonly in: Decimal;
  readonly out: Decimal;
  readonly kept: Decimal;
  readonly growth: Decimal;
  readonly cashChange: Decimal;
  readonly netWorthChange: Decimal;
}

const at = (series: Series, start: Day): Decimal => series.find((b) => b.start === start)?.amount ?? core.ZERO;

export function monthFlows(f: Pick<Forecasted, 'forecast' | 'plan' | 'today'>): MonthFlows[] {
  const c = f.forecast.cumulative;
  const p = f.forecast.cashflow.payments;
  const properties = planNs.propertyIds(f.plan);
  const mortgages = f.plan.transactions.filter((t) => t.type === 'debt' && t.isActive && t.linkedAssetId !== null && properties.has(t.linkedAssetId));
  const owedOnHomes = (day: Day): Decimal => mortgages.reduce<Decimal>((total, t) => core.add(total, core.abs(debtNs.currentBalance(t, day, f.today))), core.ZERO);
  let previous = { netWorth: f.forecast.opening.netWorth, debt: f.forecast.opening.debt, homes: owedOnHomes(core.addDays(c.netWorth[0]?.start ?? f.today, -1)) };
  return c.netWorth.map((bucket, i) => {
    const invested = at(p.investment, bucket.start);
    const moneyIn = core.add(at(p.income, bucket.start), core.max(core.ZERO, core.negate(invested)));
    const moneyOut = [at(p.expense, bucket.start), at(p.debt, bucket.start), core.max(core.ZERO, invested), at(p.property, bucket.start), at(p.depreciatingAsset, bucket.start)]
      .reduce(core.add, core.ZERO);
    const debt = c.debt[i]?.amount ?? core.ZERO;
    const homes = owedOnHomes(lastDayOf(bucket));
    const kept = core.round2(core.add(core.add(invested, core.sub(previous.debt, debt)), core.sub(previous.homes, homes)));
    const netWorthChange = core.sub(bucket.amount, previous.netWorth);
    const cashChange = core.sub(moneyIn, moneyOut);
    previous = { netWorth: bucket.amount, debt, homes };
    return {
      date: lastDayOf(bucket),
      in: moneyIn,
      out: moneyOut,
      kept,
      growth: core.sub(core.sub(netWorthChange, cashChange), kept),
      cashChange,
      netWorthChange,
    };
  });
}

/** A calendar year of the month bars (R22), from this month in the first year. */
export interface YearFlows {
  readonly year: number;
  /** The first day the year's bar counts: this month's 1st in the first year, else 1 January. */
  readonly from: Day;
  readonly partial: boolean;
  readonly in: Decimal;
  readonly out: Decimal;
  readonly kept: Decimal;
  readonly growth: Decimal;
  readonly netWorthChange: Decimal;
}

/** The month bars added up by calendar year, to the penny. */
export function yearFlows(months: readonly MonthFlows[]): YearFlows[] {
  const years = new Map<number, YearFlows>();
  for (const m of months) {
    const year = core.toYmd(m.date).year;
    const y = years.get(year) ?? {
      year, from: core.startOfMonth(m.date), partial: core.toYmd(m.date).month !== 1 && years.size === 0,
      in: core.ZERO, out: core.ZERO, kept: core.ZERO, growth: core.ZERO, netWorthChange: core.ZERO,
    };
    years.set(year, {
      ...y,
      in: core.add(y.in, m.in),
      out: core.add(y.out, m.out),
      kept: core.add(y.kept, m.kept),
      growth: core.add(y.growth, m.growth),
      netWorthChange: core.add(y.netWorthChange, m.netWorthChange),
    });
  }
  return [...years.values()];
}
